import React, { useState, useEffect } from 'react';
import styles from './BeforeAfterSlideshow.module.css';

export interface BeforeAfterSlide {
  before: string;
  after: string;
  title?: string;
  description?: string;
}

interface BeforeAfterSlideshowProps {
  slides: BeforeAfterSlide[];
  autoplay?: boolean;
  interval?: number;
  dissolveSpeed?: number;
}

const BeforeAfterSlideshow: React.FC<BeforeAfterSlideshowProps> = ({
  slides,
  autoplay = true,
  interval = 6000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [showAfter, setShowAfter] = useState(false);
  const [blindsOpen, setBlindsOpen] = useState(false);
  const [isSliding, setIsSliding] = useState(false);

  // Auto-advance slideshow
  useEffect(() => {
    if (!autoplay || slides.length === 0) return;

    const timer = setTimeout(() => {
      if (showAfter) {
        // Currently showing "after" (via blinds) - slide to next "before"
        const next = (currentIndex + 1) % slides.length;
        setNextIndex(next);
        setIsSliding(true);

        // After slide animation completes, update states
        setTimeout(() => {
          setShowAfter(false);
          setBlindsOpen(false);
          setCurrentIndex(next);
          setIsSliding(false);
        }, 800); // Match slide transition duration
      } else {
        // Currently showing "before" - start venetian blinds to "after"
        setShowAfter(true);
        // Start opening blinds after a tiny delay
        setTimeout(() => {
          setBlindsOpen(true);
        }, 50);
      }
    }, interval);

    return () => clearTimeout(timer);
  }, [currentIndex, showAfter, autoplay, interval, slides.length]);

  if (!slides || slides.length === 0) return null;

  const currentSlide = slides[currentIndex];
  const nextSlide = slides[nextIndex];

  // Create blinds
  const blindsCount = 12; // Number of horizontal blinds
  const blinds = Array.from({ length: blindsCount }, (_, i) => i);

  return (
    <div className={styles.container}>
      <div className={styles.slideshowWrapper}>
        {/* Image Container */}
        <div className={styles.imageContainer}>
          {/* Before Image - Base layer */}
          <img
            key={`before-${currentIndex}`}
            src={currentSlide.before}
            alt="Before"
            className={`${styles.image} ${styles.beforeImage}`}
          />

          {/* Venetian Blinds Effect - Stays visible once opened, slides out when transitioning */}
          {showAfter && (
            <div
              className={styles.blindsContainer}
              style={{
                transform: isSliding ? 'translateX(100%)' : 'translateX(0)',
                transition: isSliding ? 'transform 0.8s ease-in-out' : 'none',
              }}
            >
              {blinds.map((index) => {
                const delay = index * 0.05; // Stagger delay for cascade effect
                const blindHeight = 100 / blindsCount; // Height percentage of each blind

                return (
                  <div
                    key={index}
                    className={`${styles.blind} ${blindsOpen ? styles.open : ''}`}
                    style={{
                      top: `${index * blindHeight}%`,
                      height: `${blindHeight}%`,
                      transitionDelay: blindsOpen && !isSliding ? `${delay}s` : '0s',
                    }}
                  >
                    <div className={styles.blindInner}>
                      <img
                        src={currentSlide.after}
                        alt="After"
                        className={styles.blindImage}
                        style={{
                          transform: `translateY(-${index * blindHeight}%)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Next Before Image - Slides in from the left */}
          {isSliding && (
            <div 
              className={styles.slidingNextImage}
            >
              <img
                src={nextSlide.before}
                alt="Next before"
                className={styles.image}
              />
            </div>
          )}
        </div>

        {/* Indicators */}
        <div className={styles.indicators}>
          {slides.map((_, index) => (
            <div
              key={index}
              className={`${styles.indicator} ${
                index === currentIndex ? styles.active : ''
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlideshow;